import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut, Loader2, WifiOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/site/Logo";
import { api, API } from "@/lib/api";
import { cachedGet, flushQueue, useOffline } from "@/pages/tech/offline";

export const STATUS = {
  assigned: ["New job", "bg-amber-100 text-amber-800"], accepted: ["Accepted", "bg-sky-100 text-sky-800"],
  on_the_way: ["On the way", "bg-indigo-100 text-indigo-800"], arrived: ["Arrived", "bg-cyan-100 text-cyan-800"],
  in_progress: ["Repair in progress", "bg-violet-100 text-violet-800"], completed: ["Completed", "bg-emerald-100 text-emerald-800"],
  cancelled: ["Cancelled", "bg-rose-100 text-rose-800"], confirmed: ["Confirmed", "bg-sky-100 text-sky-800"], pending: ["Pending", "bg-amber-100 text-amber-800"],
  payment_received: ["Payment received", "bg-emerald-100 text-emerald-800"],
};

export const StatusPill = ({ s }) => {
  const [l, c] = STATUS[s] || [s, "bg-muted"];
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${c}`} data-testid="tech-status-pill">{l}</span>;
};

export const techFileUrl = (p) => `${API}/tech/files/${p}`;
export const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export const useTechMe = () => {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["tech", "me"], queryFn: () => cachedGet("/tech/me"), retry: false, staleTime: 60000 });
  useEffect(() => {
    if (q.isError) nav("/tech/login", { replace: true, state: { from: window.location.pathname } });
  }, [q.isError, nav]);
  return q;
};

const OfflineBar = () => {
  const qc = useQueryClient();
  const { online, pending } = useOffline();
  useEffect(() => {
    if (online) flushQueue(() => qc.invalidateQueries({ queryKey: ["tech"] }));
  }, [online, qc]);
  if (online && !pending) return null;
  return (
    <div className={`text-sm px-4 py-2 flex items-center justify-center gap-2 ${online ? "bg-sky-100 text-sky-900" : "bg-amber-100 text-amber-900"}`} data-testid="tech-offline-bar">
      {online ? <RefreshCw className="h-4 w-4 animate-spin" /> : <WifiOff className="h-4 w-4" />}
      {online ? `Syncing ${pending} saved update(s)…` : `You're offline. Updates are saved on this phone${pending ? ` (${pending} waiting)` : ""} and will sync automatically.`}
    </div>
  );
};

export const TechShell = ({ children }) => {
  const { data: me } = useTechMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const logout = async () => {
    await api.post("/tech/logout").catch(() => {});
    qc.removeQueries({ queryKey: ["tech"] });
    nav("/tech/login", { replace: true });
  };
  if (!me) return <div className="min-h-screen grid place-items-center" data-testid="tech-loading"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  return (
    <div className="min-h-screen bg-muted/30" data-testid="tech-shell">
      <header className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <Link to="/tech" data-testid="tech-home-link"><Logo className="h-8" /></Link>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold truncate" data-testid="tech-name">{me.name}</span>
            <Button variant="ghost" size="icon" onClick={logout} title="Log out" data-testid="tech-logout-button"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>
      <OfflineBar />
      <main className="max-w-5xl mx-auto px-4 py-5 pb-24">{children}</main>
    </div>
  );
};
